/**
 * PixiAppService.js - Shared Pixi Application Service
 *
 * Manages scene switching for panels that share a Pixi.js Application.
 * WorldMapPanel owns the app, DungeonPanel borrows it with a separate container.
 */

import * as PIXI from 'pixi.js';

// Reference to WorldMapPanel's app (set by WorldMapPanel)
let sharedApp = null;

// Dungeon scene container (managed here, added to WorldMap's app)
let dungeonContainer = null;

// WorldMap's original stage children (saved when switching to dungeon)
let worldMapStageChildren = [];

// Current active scene
let activeScene = 'worldMap';

/**
 * Register the WorldMapPanel's Pixi app for sharing
 * Called by WorldMapPanel after it initializes
 */
export function registerPixiApp(app) {
  sharedApp = app;
  activeScene = 'worldMap';
  console.log('[PixiAppService] WorldMap app registered');
}

/**
 * Unregister the app (called when WorldMapPanel unmounts)
 */
export function unregisterPixiApp() {
  // Clean up dungeon container if it exists
  if (dungeonContainer) {
    if (dungeonContainer.parent) {
      dungeonContainer.parent.removeChild(dungeonContainer);
    }
    dungeonContainer.destroy({ children: true });
    dungeonContainer = null;
  }
  worldMapStageChildren = [];
  sharedApp = null;
  activeScene = null;
  console.log('[PixiAppService] WorldMap app unregistered');
}

/**
 * Get the shared Pixi application
 */
export function getPixiApp() {
  return sharedApp;
}

/**
 * Get or create the dungeon scene container
 */
export function getDungeonContainer() {
  if (!dungeonContainer && sharedApp) {
    dungeonContainer = new PIXI.Container();
    dungeonContainer.label = 'DungeonScene';
    dungeonContainer.sortableChildren = true;
  }
  return dungeonContainer;
}

/**
 * Switch to the dungeon scene
 * Hides WorldMap content, shows Dungeon content
 */
export function showDungeonScene() {
  if (!sharedApp) {
    console.warn('[PixiAppService] Cannot switch to dungeon - no app registered');
    return false;
  }

  if (activeScene === 'dungeon') {
    return true; // Already in dungeon
  }

  // Save WorldMap's stage children and hide them
  worldMapStageChildren = [...sharedApp.stage.children];
  worldMapStageChildren.forEach(child => {
    child.visible = false;
  });

  // Create dungeon container if needed
  if (!dungeonContainer) {
    dungeonContainer = new PIXI.Container();
    dungeonContainer.label = 'DungeonScene';
    dungeonContainer.sortableChildren = true;
  }

  // Add dungeon container to stage
  dungeonContainer.visible = true;
  if (!dungeonContainer.parent) {
    sharedApp.stage.addChild(dungeonContainer);
  }

  activeScene = 'dungeon';
  console.log('[PixiAppService] Switched to Dungeon scene');
  return true;
}

/**
 * Switch back to the world map scene
 * Hides Dungeon content, shows WorldMap content
 */
export function showWorldMapScene() {
  if (!sharedApp) {
    console.warn('[PixiAppService] Cannot switch to world map - no app registered');
    return false;
  }

  if (activeScene === 'worldMap') {
    return true; // Already in world map
  }

  // Hide dungeon container
  if (dungeonContainer) {
    dungeonContainer.visible = false;
    if (dungeonContainer.parent) {
      sharedApp.stage.removeChild(dungeonContainer);
    }
  }

  // Restore WorldMap's stage children visibility
  worldMapStageChildren.forEach(child => {
    child.visible = true;
  });

  activeScene = 'worldMap';
  console.log('[PixiAppService] Switched to WorldMap scene');
  return true;
}

/**
 * Get the current active scene name
 */
export function getActiveScene() {
  return activeScene;
}

/**
 * Check if app is registered and ready
 */
export function isReady() {
  return sharedApp !== null && !sharedApp.renderer?.destroyed;
}

/**
 * Clear the dungeon scene content (but keep container)
 */
export function clearDungeonScene() {
  if (dungeonContainer) {
    dungeonContainer.removeChildren();
  }
}
